import React from "react";
import { connect } from "react-redux";
import { Formik } from "formik";
import { Form } from "react-bootstrap";
import * as Yup from "yup";

import { FormCollapsible, Select, StateSelect, SubmitSearchButton, TextInput } from "components/forms";
import { findContactInformation, hideModal, saveDiscoveryDataInfo } from "actions";
import Table from "components/table";
import { FaArrowLeft, FaArrowRight, FaArrowUp } from "react-icons/fa";
import { FIND_CONTACT_INFORMATION_MODAL } from "components/modals";
import { activeAccountIdSelector } from "../../../reducers";

const FindContactInformationModalForm = ({ accountInfo, ...props }) => {
  const [contactInfo, setContactInfo] = React.useState([]);
  const [page, setPage] = React.useState(1);

  const ContactInfoTable = () => {
    if (!contactInfo) {
      return null;
    }

    const columns = [
      {
        Header: "Select",
        Cell: ({ row }) => {
          const data = {
            first_name: row.original.FirstName,
            middle_name: row.original.MiddleName,
            last_name: row.original.LastName,
            npn: row.original.NPN,
            phone: row.original.PrimaryPhone,
            email: row.original.Email_BusinessType,
            address: row.original.Primary_Address1,
            personal_phone: row.original.Home_Phone,
            personal_email: row.original.Email_PersonalType,
            home_address: row.original.Home_Address1,
          };
          return (
            <button
              className={`btn btn--secondary`}
              onClick={e => {
                props.hideModal(FIND_CONTACT_INFORMATION_MODAL);
                props.saveDiscoveryDataInfo(props.activeAccountId, data);
              }}
            >
              Save as Notes
            </button>
          );
        },
      },
      {
        Header: "First Name",
        accessor: "FirstName",
        className: "wrap-text",
      },
      {
        Header: "Middle Name",
        accessor: "MiddleName",
        className: "wrap-text",
      },
      {
        Header: "Last Name",
        accessor: "LastName",
        className: "wrap-text",
      },
      {
        Header: "NPN",
        accessor: "NPN",
        className: "wrap-text",
      },
      {
        Header: "Phone",
        accessor: "Primary_Phone",
        className: "wrap-text",
      },
      {
        Header: "Email",
        accessor: "Email_BusinessType",
        className: "wrap-text",
      },
      {
        Header: "Address",
        accessor: "Primary_Address1",
        className: "wrap-text",
      },
      {
        Header: "Personal Phone",
        accessor: "Home_Phone",
        className: "wrap-text",
      },
      {
        Header: "Personal Email",
        accessor: "Email_PersonalType",
        className: "wrap-text",
      },
      {
        Header: "Personal Address",
        accessor: "Home_Address1",
        className: "wrap-text",
      },
    ];

    let resultsString = `Results: ${page * 100 - 99} - ${page * 100}`;
    return (
      <>
        <div id="results">
          {contactInfo.length > 1 ? (
            <div style={{ fontSize: 16, marginBottom: 5, paddingTop: 10 }}>{resultsString}</div>
          ) : null}
        </div>
        <Table
          columns={columns}
          data={contactInfo}
          defaultSort={true}
          showPaginationResults={false}
          defaultPagination={true}
          emptyMessage={"No results"}
        />
      </>
    );
  };

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required("Required"),
    last_name: Yup.string().required("Required"),
  });

  return (
    <Formik
      enableReinitialize
      initialValues={{
        first_name: accountInfo.first_name,
        last_name: accountInfo.last_name,
        npn: accountInfo.national_producer_number,
        phone: accountInfo.cell_phone,
        email: accountInfo.work_email,
        business_city: accountInfo.city,
        business_state: accountInfo.state,
        home_city: accountInfo.city,
        home_state: accountInfo.state,
      }}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting, setErrors }) => {
        document.activeElement.blur(); // Remove focus from the currently focused element
        let data = await props.findContactInformation(values);
        setContactInfo(data.results);
      }}
    >
      {({ handleSubmit, values }) => {
        const BackButton = () => {
          const [loading, setLoading] = React.useState(false);

          const handleClick = async () => {
            try {
              setLoading(true);
              if (contactInfo.length >= 100) {
                let data = await props.findContactInformation(...values);
                setPage(page - 1);
                setContactInfo(data.results);
                const inputGroupElement = document.querySelector("#results");
                if (inputGroupElement) {
                  inputGroupElement.focus();
                  inputGroupElement.scrollIntoView();
                }
              }
            } catch (error) {
              console.error("Error fetching contactInfo:", error);
            } finally {
              setLoading(false);
            }
          };

          if (contactInfo.length >= 100 && page > 1) {
            return (
              <button
                className={`btn btn--secondary`}
                style={{ backgroundColor: "blue" }}
                onClick={handleClick}
                disabled={loading}
              >
                <FaArrowLeft style={{ marginLeft: "5px" }} />
                {loading ? "Loading..." : "Back"}
              </button>
            );
          } else {
            return <button style={{ opacity: 0 }}>-</button>;
          }
        };
        const NewSearchButton = () => {
          if (!contactInfo.length) {
            return null;
          }
          return (
            <button
              className={`btn btn--secondary`}
              style={{ marginRight: "10px" }}
              onClick={() => {
                // To Scroll the Modal all the way up, we find the "number" input and set the browser's focus on it
                const inputGroupElement = document.querySelector('input[name="number"]');
                if (inputGroupElement) {
                  inputGroupElement.focus();
                }
              }}
            >
              <FaArrowUp style={{ marginLeft: "5px" }} />
              Start a New Search
            </button>
          );
        };
        const NextButton = () => {
          const [loading, setLoading] = React.useState(false);

          const handleClick = async () => {
            try {
              setLoading(true);
              if (contactInfo.length >= 100) {
                let data = await props.findContactInformation(...values);
                setPage(page + 1);
                setContactInfo(data.results);
                const inputGroupElement = document.querySelector("#results");
                if (inputGroupElement) {
                  inputGroupElement.focus();
                  inputGroupElement.scrollIntoView();
                }
              }
            } catch (error) {
              console.error("Error fetching contactInfo:", error);
            } finally {
              setLoading(false);
            }
          };

          if (contactInfo.length >= 100 && page < 21) {
            return (
              <button
                className={`btn btn--secondary`}
                style={{ backgroundColor: "blue" }}
                onClick={handleClick}
                disabled={loading}
              >
                {loading ? "Loading..." : "More"}
                <FaArrowRight style={{ marginLeft: "5px" }} />
              </button>
            );
          } else {
            return <button style={{ opacity: 0 }}>-</button>;
          }
        };

        return (
          <>
            <FormCollapsible onSubmit={handleSubmit} title="Search NPI Records">
              <Form.Row>
                <TextInput label="First Name" name="first_name" isRequired={true} />
                <TextInput label="Middle Name" name="middle_name" />
                <TextInput label="Last Name" name="last_name" disabled={true} isRequired={true} />
              </Form.Row>
              <Form.Row>
                <TextInput label="NPN" name="npn" />
                <TextInput label="E-mail" name="email" />
                <TextInput label="Phone" name="phone" />
              </Form.Row>
              <Form.Row>
                <TextInput label="Business City" name="business_city" />
                <StateSelect label="Business State" name="business_state" />
                <TextInput label="Home City" name="home_city" />
                <StateSelect label="Home State" name="home_state" />
              </Form.Row>
              <SubmitSearchButton />
            </FormCollapsible>
            <ContactInfoTable />
            <Form.Row className="d-flex justify-content-between align-items-center">
              {contactInfo.length >= 100 ? (
                <>
                  <BackButton />
                  <NextButton />
                </>
              ) : null}
            </Form.Row>
            <Form.Row className="justify-content-center">
              <NewSearchButton />
            </Form.Row>
          </>
        );
      }}
    </Formik>
  );
};

const mapStateToProps = state => {
  return {
    activeAccountId: activeAccountIdSelector(state),
  };
};

export default connect(mapStateToProps, {
  findContactInformation,
  hideModal,
  saveDiscoveryDataInfo,
})(FindContactInformationModalForm);
